<script setup lang="ts">
type FormType = 'text' | 'password' | 'email' | 'tel'
type Size = 'medium' | 'large'

type Props = {
  modelValue: string
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  type?: FormType
  errorMessage?: string
  size?: Size
  autoFocus?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  disabled: false,
  readonly: false,
  type: 'text',
  color: 'none',
  errorMessage: '',
  size: 'medium',
  autoFocus: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  blur: []
}>()

const vFocus = {
  mounted: (el: HTMLInputElement) => {
    if (props.autoFocus) {
      el.focus()
    }
  },
}

const isChanged = ref(false)

const value = computed({
  get: () => props.modelValue || '',
  set: (value: string) => {
    isChanged.value = true
    emit('update:modelValue', value)
  },
})

const hasError = computed(() => !!props.errorMessage)

const displayError = computed(() => isChanged.value && hasError.value)
</script>

<template>
  <div>
    <label
      class="input base-input"
      :class="{
        'is-danger': displayError,
        large: size === 'large',
        'is-disabled': disabled,
      }"
    >
      <input
        v-model="value"
        v-focus
        class="input base-input-inner"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        @blur="emit('blur')"
      />
      <slot name="suffix" />
    </label>
    <span v-if="displayError" class="error-message">{{ errorMessage }}</span>
  </div>
</template>

<style scoped>
.base-input {
  display: flex;
  align-items: center;
  gap: 4px;

  /* bulma の既定枠線色(#dbdbdb)をトークンに上書き */
  border-color: var(--semantic-tokens-neutral-weak-border);

  /* フォーカス時の枠線（bulma の $link 青を新パレットのトークンに） */
  &:focus-within {
    border-color: var(--semantic-tokens-brand-strong-text);
  }

  /* bulma のエラー枠線をトークンに上書き */
  &.is-danger {
    border-color: var(--semantic-tokens-danger-strong-border);
  }
}

.error-message {
  color: var(--semantic-tokens-danger-default-text);
}

.is-disabled {
  background-color: var(--semantic-tokens-neutral-xweak-surface);
  border-color: var(--semantic-tokens-neutral-weak-border);
  box-shadow: none;
  color: var(--semantic-tokens-neutral-weak-text);
  cursor: not-allowed;
}

.base-input-inner {
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 100%;
  border: none;
  box-shadow: none;
  background: transparent;
  font-size: inherit;
  color: inherit;
  padding: 0;

  &:focus {
    outline: none;
  }
}

.large {
  font-weight: bold;
  font-size: 1.25rem;
}
</style>
